/**
 * SEA — the sea gradient + animated wave strokes + the sun/moon glare column.
 */

import {
  clamp01,
  lerpRgb,
  rgb,
  normalizeTimeOfDay,
  timeSec,
} from "./sky-utils.js";

// Sea palettes from day.html / night.html (--sea-top / --sea-mid / --sea-deep);
// `wave` is the foam-stroke colour.
var SEA_KEYFRAMES = [
  { t: 0, surface: [127, 196, 210], mid: [90, 165, 195], deep: [60, 130, 170], wave: [223, 246, 255] },
  { t: 0.25, surface: [127, 210, 238], mid: [70, 179, 220], deep: [44, 143, 196], wave: [223, 246, 255] },
  { t: 0.5, surface: [80, 120, 160], mid: [120, 110, 130], deep: [60, 70, 100], wave: [255, 200, 160] },
  { t: 0.75, surface: [21, 50, 78], mid: [14, 39, 64], deep: [8, 26, 46], wave: [120, 150, 190] },
  { t: 1, surface: [127, 196, 210], mid: [90, 165, 195], deep: [60, 130, 170], wave: [223, 246, 255] },
];

/** Radians of wave scroll per unit of `waveTime` (seconds) passed to `drawSea`. */
var SEA_WAVE_RATE = 1.2;

/** Sea colour bled up into the sky (px at ui-w=720). This is the horizon soften — do not mix sky down into the water. */
export var HORIZON_BLEED_WIDTH_AT_720 = 14;
export var HORIZON_HAZE_WIDTH_AT_720 = HORIZON_BLEED_WIDTH_AT_720;
export var HORIZON_HAZE_PEAK_ALPHA = 0;
export var HORIZON_LINE_HEIGHT_AT_720 = 0;
export var HORIZON_LINE_PEAK_ALPHA = 0;
/** Tiny AA on the sea fill only. */
export var HORIZON_SEAM_WIDTH_AT_720 = 3;

export function horizonBleedHeight(width) {
  var w = Math.max(0, Number(width) || 0);
  return Math.max(1, (w / 720) * HORIZON_BLEED_WIDTH_AT_720);
}

export function horizonHazeHeight(width) {
  return horizonBleedHeight(width);
}

export function horizonSeamHeight(width) {
  var w = Math.max(0, Number(width) || 0);
  return Math.max(0.5, (w / 720) * HORIZON_SEAM_WIDTH_AT_720);
}

/** Sea glare tuning — aligned to day.html / mockup-scale.css (--u-sea-glare-* at ui-w=720). */
var SEA_GLARE_TUNING = {
  dayWidthRatio: 200 / 720,
  nightWidthRatio: 160 / 720,
  lineWidthRatio: 42 / 720,
  rippleHeightRatio: 28 / 720,
  pulsePeriodMs: 5000,
  ripplePeriodMs: 4500,
  pulseOpacityMin: 0.88,
  pulseOpacityMax: 1,
  rippleScaleMin: 1,
  rippleScaleMax: 1.06,
  blurPx: 11,
  lineBlurPx: 5,
  rippleBlurPx: 4,
};

function sampleSeaPair(t) {
  t = normalizeTimeOfDay(t);
  for (var i = 0; i < SEA_KEYFRAMES.length - 1; i++) {
    var a = SEA_KEYFRAMES[i];
    var b = SEA_KEYFRAMES[i + 1];
    if (t >= a.t && t <= b.t) {
      var span = b.t - a.t;
      var u = span > 0 ? (t - a.t) / span : 0;
      return {
        surface: lerpRgb(a.surface, b.surface, u),
        mid: lerpRgb(a.mid, b.mid, u),
        deep: lerpRgb(a.deep, b.deep, u),
        wave: lerpRgb(a.wave, b.wave, u),
      };
    }
  }
  var last = SEA_KEYFRAMES[SEA_KEYFRAMES.length - 1];
  return {
    surface: last.surface.slice(),
    mid: last.mid.slice(),
    deep: last.deep.slice(),
    wave: last.wave.slice(),
  };
}

function glarePulseAlpha(currentTime) {
  var t = timeSec(currentTime);
  var phase = (t * 1000 % SEA_GLARE_TUNING.pulsePeriodMs) / SEA_GLARE_TUNING.pulsePeriodMs;
  var wave = 0.5 - 0.5 * Math.cos(phase * Math.PI * 2);
  return SEA_GLARE_TUNING.pulseOpacityMin
    + wave * (SEA_GLARE_TUNING.pulseOpacityMax - SEA_GLARE_TUNING.pulseOpacityMin);
}

/**
 * Soften the sky just above the sea line by fading in sea-surface colour.
 * Draw this *behind* the sun/moon and *behind* the opaque sea fill.
 */
export function drawHorizonBleed(ctx, bounds, timeOfDay, opts) {
  if (!ctx || !bounds) return;
  opts = opts || {};
  var x = bounds.x;
  var y = bounds.y;
  var w = Math.max(0, bounds.width);
  var h = Math.max(0, bounds.height);
  if (w <= 0 || h <= 0) return;
  var seaLineY = Number(opts.seaLineY);
  var seaY = isFinite(seaLineY) ? seaLineY : y + h - h * 0.35;
  var colors = sampleSeaPair(timeOfDay);
  var bleedH = horizonBleedHeight(w);
  var bleedGrad = ctx.createLinearGradient(x, seaY - bleedH, x, seaY);
  bleedGrad.addColorStop(0, rgb(colors.surface, 0));
  bleedGrad.addColorStop(0.55, rgb(colors.surface, 0.4));
  bleedGrad.addColorStop(1, rgb(colors.surface, 1));
  ctx.fillStyle = bleedGrad;
  ctx.fillRect(x, seaY - bleedH, w, bleedH);
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ x: number, y: number, width: number, height: number }} bounds
 * @param {number} timeOfDay
 * @param {{ seaLineY?: number, waveTime?: number, scaleUi?: number }} [opts]
 */
export function drawSea(ctx, bounds, timeOfDay, opts) {
  if (!ctx || !bounds) return;
  opts = opts || {};
  var x = bounds.x;
  var y = bounds.y;
  var w = Math.max(0, bounds.width);
  var h = Math.max(0, bounds.height);
  if (w <= 0 || h <= 0) return;

  var seaLineY = Number(opts.seaLineY);
  var seaY;
  var seaH;
  if (isFinite(seaLineY)) {
    seaY = seaLineY;
    seaH = Math.max(0, y + h - seaY);
  } else {
    seaH = h * 0.35;
    seaY = y + h - seaH;
  }
  var colors = sampleSeaPair(timeOfDay);

  // One continuous sea body: surface → mid → deep with extra stops so the 45%
  // join does not read as a second horizon.
  var grad = ctx.createLinearGradient(x, seaY, x, seaY + seaH);
  grad.addColorStop(0, rgb(colors.surface));
  grad.addColorStop(0.22, rgb(lerpRgb(colors.surface, colors.mid, 0.5)));
  grad.addColorStop(0.45, rgb(colors.mid));
  grad.addColorStop(0.72, rgb(lerpRgb(colors.mid, colors.deep, 0.5)));
  grad.addColorStop(1, rgb(colors.deep));
  ctx.fillStyle = grad;
  ctx.fillRect(x, seaY, w, seaH);

  // Wave-row count is driven by the available sea HEIGHT: more visible sea (taller
  // screen) → more rows; a thin band (horizon near the bottom) → just a few. Row
  // spacing and wave wavelength scale with the LAYOUT scale (the game's coreRect,
  // not raw screen width) so the waves stay visually proportional to the rest of
  // the UI on any aspect ratio. An absolute cap keeps a very tall band from
  // over-filling, and prominence fades the whole thing down as the band shrinks.
  var scaleUi = Number(opts.scaleUi);
  if (!isFinite(scaleUi) || scaleUi <= 0) scaleUi = Math.max(0.5, w / 720);
  var minRowSpacing = 9 * scaleUi;
  var maxRowsByHeight = Math.floor((seaH * 0.92) / minRowSpacing);
  var waveCount = Math.max(1, Math.min(maxRowsByHeight, 26));
  var prominence = clamp01(seaH / (h * 0.16));
  // Smaller, tighter chop: low amplitude, fixed-ish wavelength in scaled px so the
  // crest count grows with width instead of always showing the same few humps.
  var amp = Math.max(0.6, seaH * 0.009);
  var waveLengthPx = 120 * scaleUi;
  var cycles = Math.max(3, w / waveLengthPx);
  // Wave scroll phase: when the caller supplies a continuous `waveTime` (seconds),
  // drive the waves from it so motion is tied to that clock (and pauses when it
  // pauses). Otherwise fall back to the legacy color-phase wobble so existing
  // callers are unaffected.
  var waveTime = Number(opts.waveTime);
  var basePhase = isFinite(waveTime)
    ? waveTime * SEA_WAVE_RATE
    : normalizeTimeOfDay(timeOfDay) * Math.PI * 2;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, seaY, w, seaH);
  ctx.clip();

  // Resolve enough horizontal samples per crest that even short wavelengths stay
  // smooth, then step the polyline by that. Always land on px === w so the ribbon
  // top edge is periodic (sin(2π·cycles) === sin(0)) before closing vertically.
  var step = Math.max(3, w / (cycles * 8));
  function waveSurfaceY(px, rowY, phase) {
    return rowY + Math.sin((px / w) * Math.PI * 2 * cycles + phase) * amp;
  }
  for (var wi = 0; wi < waveCount; wi++) {
    var depth = waveCount > 1 ? wi / (waveCount - 1) : 0; // 0 = near horizon
    // Cluster rows near the horizon and spread them out toward the bottom: a
    // power curve maps evenly-indexed rows to positions that bunch up near the
    // top of the sea band (more waves at the horizon, fewer far below it).
    var rowFrac = Math.pow(depth, 1.8);
    var rowY = seaY + seaH * (0.04 + rowFrac * 0.9);
    var phase = wi * 1.7 + basePhase;
    // Subtle overall, a touch stronger near the horizon, dimmer when the band
    // is thin.
    var alpha = (0.06 + 0.04 * (1 - depth)) * (0.35 + 0.65 * prominence);
    // Each wave is a thin translucent ribbon rather than a bare line: a vertical
    // gradient from the foam colour at the crest fading to clear over a small
    // band, so overlapping rows read as stacked layers of water.
    var bandH = amp * 2 + (3 + 2 * (1 - depth)) * scaleUi;
    var ribbon = ctx.createLinearGradient(0, rowY - amp, 0, rowY + bandH);
    ribbon.addColorStop(0, rgb(colors.wave, alpha * 1.4));
    ribbon.addColorStop(0.35, rgb(colors.wave, alpha));
    ribbon.addColorStop(1, rgb(colors.wave, 0));
    ctx.fillStyle = ribbon;
    ctx.beginPath();
    ctx.moveTo(x, waveSurfaceY(0, rowY, phase));
    var px = 0;
    while (px < w) {
      var nextPx = Math.min(w, px + step);
      ctx.lineTo(x + nextPx, waveSurfaceY(nextPx, rowY, phase));
      px = nextPx;
    }
    // Close the ribbon along the bottom of the band.
    ctx.lineTo(x + w, rowY + bandH);
    ctx.lineTo(x, rowY + bandH);
    ctx.closePath();
    ctx.fill();
  }

  ctx.restore();
}

/**
 * Sun/moon column glare on sea — mockup-tuned shimmer at horizon.
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ x: number, y: number, width: number, height: number }} bounds
 * @param {{ celestialX: number, seaLineY: number, isMoon?: boolean, currentTime?: number, strength?: number }} opts
 */
export function drawSeaGlare(ctx, bounds, opts) {
  if (!ctx || !bounds || !opts) return;
  var cx = Number(opts.celestialX);
  var seaLineY = Number(opts.seaLineY);
  if (!isFinite(cx) || !isFinite(seaLineY)) return;

  var strength = opts.strength != null ? clamp01(opts.strength) : 1;
  if (strength <= 0.01) return;

  var w = bounds.width;
  var h = bounds.height;
  var seaY = isFinite(seaLineY)
    ? seaLineY
    : bounds.y + h - h * 0.35;
  if (seaLineY < seaY || seaLineY > bounds.y + h) return;

  var isMoon = !!opts.isMoon;
  var scale = Math.max(0.5, w / 720);
  // Width grows as the body rises (caller passes 0..1); keeps a thin sliver at
  // the first tip so the glare appears/disappears as a narrowing-then-widening band.
  var widthScale = opts.widthScale != null ? clamp01(opts.widthScale) : 1;
  var baseHalfW = (isMoon ? SEA_GLARE_TUNING.nightWidthRatio : SEA_GLARE_TUNING.dayWidthRatio)
    * w * 0.5 * widthScale;
  var pulseA = glarePulseAlpha(opts.currentTime) * strength;
  var tSec = timeSec(opts.currentTime);

  var top = seaY;
  var height = bounds.y + h - seaY;
  if (height <= 0 || baseHalfW <= 0.5) return;
  var bleedH = horizonBleedHeight(w);

  // Warm sun vs cool moon palette: a near-white hot core and a tinted halo.
  var core = isMoon ? [240, 247, 255] : [255, 234, 158];
  var tint = isMoon ? [200, 222, 250] : [255, 198, 80];
  var peak = isMoon ? 0.5 : 0.82;

  ctx.save();
  ctx.beginPath();
  ctx.rect(bounds.x, top - bleedH, w, height + bleedH);
  ctx.clip();
  // Additive blending so overlapping glints build into bright sparkles and the
  // whole path glows rather than painting flat opaque rectangles.
  ctx.globalCompositeOperation = "lighter";

  // Soft horizon hotspot: a flattened radial bloom right where the body meets the
  // sea — the brightest part of the reflection, with no hard edges.
  paintBlob(ctx, cx, top, baseHalfW * 1.5, height * 0.07,
    core, tint, peak * pulseA);

  // Glitter path: a stack of flattened light blobs marching down the water. Each
  // row sways a little and twinkles on its own phase, fanning wider and fading as
  // it nears the viewer, which reads as a shimmering sun/moon trail.
  var rows = Math.max(12, Math.round(height / (9 * scale)));
  var rowThick = (height / rows) * 1.9;
  for (var i = 0; i < rows; i++) {
    var d = i / (rows - 1);                 // 0 at horizon → 1 at the bottom
    var ry = top + d * height;
    var widthShimmer = 0.82 + 0.18 * Math.sin(tSec * 1.6 + i * 0.9);
    var halfW = baseHalfW * (0.32 + 1.15 * d) * widthShimmer;
    var sway = Math.sin(tSec * 1.05 + i * 0.28) * baseHalfW * 0.12;
    var fade = Math.pow(1 - d, 1.25);       // brighter near the horizon
    var twinkle = 0.5 + 0.5 * Math.sin(tSec * 2.1 + i * 1.7);
    var a = peak * pulseA * fade * (0.45 + 0.55 * twinkle);
    if (a <= 0.004) continue;
    paintBlob(ctx, cx + sway, ry, halfW, rowThick, core, tint, a);
  }

  ctx.restore();
}

/**
 * Paint one soft, horizontally-stretched light blob (a vertically-squashed radial
 * gradient): a white-hot core fading through `tint` to transparent. Used to build
 * the sea reflection out of overlapping glints with no hard edges.
 */
function paintBlob(ctx, x, y, halfW, halfH, core, tint, alpha) {
  if (halfW <= 0 || halfH <= 0 || alpha <= 0) return;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(1, halfH / halfW); // squash the circle into a horizontal lens
  var g = ctx.createRadialGradient(0, 0, 0, 0, 0, halfW);
  g.addColorStop(0, rgb(core, alpha));
  g.addColorStop(0.45, rgb(tint, alpha * 0.5));
  g.addColorStop(1, rgb(tint, 0));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, halfW, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
